import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    padding: "2px",
    border: "1px solid",
    borderColor: theme.palette.background.paper,
    left: 0,
    width: "100%",
    maxHeight: "150px",
    overflowY: "auto",
    "& li": {
      listStyle: "none",
      "& span": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": {
          backgroundColor: theme.palette.background.default,
          cursor: "pointer",
        },
      },
    },
  },

  messageQuickAnswersWrapperItemAnchor: {
    color: theme.palette.text.primary,
  },

  selectedMessageQuickAnswersWrapperItemAnchor: {
    backgroundColor: theme.palette.background.selectedTicket,
  },
}));

const QuickAnswersList = ({
  quickAnswers,
  handleQuickAnswersClick,
  typeBar,
  setTypeBar,
  filter
}) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const quickAnswersRef = useRef([]);

  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [structuredFilter, setStructuredFilter] = useState("");



  //  ***********
  //  ** Memos **
  //  ***********
  const filteredQuickAnswers = useMemo(() => {
    const lowerFilter = structuredFilter.toLowerCase();

    return quickAnswers.filter(value => {
      const lowerShortcut = value.shortcut.toLowerCase();

      if (lowerFilter !== "" && !lowerShortcut.includes(lowerFilter)) return false;
      return true;
    });
  }, [quickAnswers, structuredFilter]);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (!typeBar) return;
    setStructuredFilter(filter.substring(1));
  }, [typeBar, filter]);

  useEffect(() => {
    setFocusedIndex(0);
  }, [structuredFilter]);

  useEffect(() => {
    if (!typeBar) return;

    const handleKeyDown = (event) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedIndex(previousIndex => previousIndex < filteredQuickAnswers.length - 1 ? previousIndex + 1 : 0);
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedIndex(previousIndex => previousIndex > 0 ? previousIndex - 1 : filteredQuickAnswers.length - 1);
      }

      if (event.key === "Enter" && filteredQuickAnswers[focusedIndex]) {
        event.preventDefault();
        handleQuickAnswersClick(filteredQuickAnswers[focusedIndex].message);
        setTypeBar(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [typeBar, filteredQuickAnswers, focusedIndex, setTypeBar, handleQuickAnswersClick]);

  useEffect(() => {
    if (!typeBar) return;

    const handleClick = (event) => {
      const quickAnswersList = document.getElementById("quickAnswersList");
      const shouldHideAnswersList = quickAnswersList && !(quickAnswersList.contains(event.target));

      if (shouldHideAnswersList) setTypeBar(false);
    };

    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [typeBar, setTypeBar]);

  useEffect(() => {
    if (!typeBar) return;

    const currentQuickAnswer = quickAnswersRef.current[focusedIndex];
    if (focusedIndex >= 0 && currentQuickAnswer) currentQuickAnswer.scrollIntoView({ block: "nearest" });
  }, [typeBar, focusedIndex]);




  //  ************
  //  ** Return **
  //  ************
  if (!typeBar) return (<></>);

  return (
    <ul id="quickAnswersList" className={classes.messageQuickAnswersWrapper}>
      {filteredQuickAnswers.map((value, index) => (
        <li key={index} ref={element => (quickAnswersRef.current[index] = element)}>
          <span
            className={index === focusedIndex
              ? `${classes.messageQuickAnswersWrapperItemAnchor} ${classes.selectedMessageQuickAnswersWrapperItemAnchor}`
              : classes.messageQuickAnswersWrapperItemAnchor
            }
            onClick={() => handleQuickAnswersClick(value.message)}
            onMouseEnter={() => setFocusedIndex(index)}
          >
            {`${value.shortcut} - ${value.message}`}
          </span>
        </li>
      ))}
    </ul>
  );
};

export default QuickAnswersList;
